import { z } from 'zod';
import { parseFrontmatter } from './frontmatter';
import type { Language } from '@/shared/utils/i18n';

/**
 * 基于目录结构的内容系统（"@/content"）。
 *
 * 约定：
 * - 内容放在仓库根目录 content/ 下，构建期通过 import.meta.glob 打包
 * - 文件名 <slug>.<lang>.md（如 dashucl.en.md / dashucl.zh.md）
 * - 中文缺失时回退到英文
 * - frontmatter 用 zod 校验；运行时校验失败会跳过该文件并 console.error
 *   （严格校验由 src/lib/content.test.ts 在 CI 中兜底，避免线上白屏）
 */

// ---------- Schemas ----------

const linksSchema = z
  .object({
    github: z.string().url().optional(),
    external: z.string().url().optional(),
    pdf: z.string().optional()
  })
  .optional();

export const postFrontmatterSchema = z.object({
  title: z.string(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  summary: z.string(),
  category: z.string().optional(),
  tags: z.array(z.string()).default([]),
  draft: z.boolean().default(false),
  links: linksSchema
});

export const projectFrontmatterSchema = postFrontmatterSchema.extend({
  status: z.enum(['active', 'shipped', 'archived', 'contributed']),
  role: z.string().optional(),
  stack: z.array(z.string()).default([]),
  period: z.string()
});

export const portfolioItemSchema = z.object({
  src: z.string(),
  alt: z.string(),
  caption: z.string().optional(),
  date: z.string().optional()
});

export type PostMeta = z.infer<typeof postFrontmatterSchema>;
export type ProjectMeta = z.infer<typeof projectFrontmatterSchema>;
export type PortfolioItem = z.infer<typeof portfolioItemSchema>;

export interface ContentEntry<M> {
  slug: string;
  lang: Language;
  meta: M;
  body: string;
}

// ---------- Loading ----------

type RawFiles = Record<string, string>;

// 下划线开头的文件（_draft.en.md）在构建期就被排除，正文不会进入公开 bundle。
// frontmatter 的 draft: true 只是"不在列表显示"，内容仍会被打包——保密草稿必须用 _ 前缀。
const postFiles = import.meta.glob(['/content/posts/*.md', '!**/_*'], {
  query: '?raw',
  import: 'default',
  eager: true
}) as RawFiles;

const projectFiles = import.meta.glob(['/content/projects/*.md', '!**/_*'], {
  query: '?raw',
  import: 'default',
  eager: true
}) as RawFiles;

const spaceBlogFiles = import.meta.glob(['/content/space/blog/*.md', '!**/_*'], {
  query: '?raw',
  import: 'default',
  eager: true
}) as RawFiles;

export const FILENAME_RE = /([^/]+)\.(en|zh)\.md$/;

function buildCollection<S extends z.ZodTypeAny>(
  files: RawFiles,
  schema: S
): Map<string, Partial<Record<Language, ContentEntry<z.output<S>>>>> {
  type M = z.output<S>;
  const collection = new Map<string, Partial<Record<Language, ContentEntry<M>>>>();

  for (const [path, raw] of Object.entries(files)) {
    const match = FILENAME_RE.exec(path);
    if (!match) {
      console.error(`[content] 文件名不符合 <slug>.<en|zh>.md 约定，已跳过: ${path}`);
      continue;
    }
    const slug = match[1]!;
    const lang = match[2] as Language;

    const { data, content } = parseFrontmatter(raw);
    const result = schema.safeParse(data);
    if (!result.success) {
      console.error(`[content] frontmatter 校验失败，已跳过 ${path}:`, result.error.issues);
      continue;
    }

    const entry: ContentEntry<M> = { slug, lang, meta: result.data, body: content.trim() };
    const existing = collection.get(slug) ?? {};
    existing[lang] = entry;
    collection.set(slug, existing);
  }

  return collection;
}

const posts = buildCollection(postFiles, postFrontmatterSchema);
const projects = buildCollection(projectFiles, projectFrontmatterSchema);
const spaceBlog = buildCollection(spaceBlogFiles, postFrontmatterSchema);

function resolve<M>(
  collection: Map<string, Partial<Record<Language, ContentEntry<M>>>>,
  lang: Language
): ContentEntry<M>[] {
  const entries: ContentEntry<M>[] = [];
  for (const variants of collection.values()) {
    const entry = variants[lang] ?? variants.en;
    if (entry) entries.push(entry);
  }
  return entries;
}

function listEntries<M extends { date: string; draft: boolean }>(
  collection: Map<string, Partial<Record<Language, ContentEntry<M>>>>,
  lang: Language
): ContentEntry<M>[] {
  return resolve(collection, lang)
    .filter(entry => !(import.meta.env.PROD && entry.meta.draft))
    .sort((a, b) => b.meta.date.localeCompare(a.meta.date));
}

// ---------- Public API ----------

export function getPosts(lang: Language): ContentEntry<PostMeta>[] {
  return listEntries(posts, lang);
}

export function getPost(slug: string, lang: Language): ContentEntry<PostMeta> | undefined {
  return getPosts(lang).find(entry => entry.slug === slug);
}

export function getProjects(lang: Language): ContentEntry<ProjectMeta>[] {
  return listEntries(projects, lang);
}

export function getSpaceBlogPosts(lang: Language): ContentEntry<PostMeta>[] {
  return listEntries(spaceBlog, lang);
}

export function getSpaceBlogPost(
  slug: string,
  lang: Language
): ContentEntry<PostMeta> | undefined {
  return getSpaceBlogPosts(lang).find(entry => entry.slug === slug);
}

// ---------- 测试用：暴露原始文件表，供 content.test.ts 做严格校验 ----------

export const __raw = { postFiles, projectFiles, spaceBlogFiles };
